import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import times from 'lodash/times';
import moment from 'moment';
import {getWindow} from 'ssr-window';
import EnvironmentUtils from '@spothero/utils/environment';
import StorageUtils from '@spothero/utils/storage';
import UrlUtils from '@spothero/utils/url';
import Config from 'config';
import {Page} from 'utils/page-utils';
import DateUtils from 'utils/date';
import TimeUtils from 'utils/time';
import SearchTileUtils from './search-tile-utils';

const window = getWindow();
const isBrowser = EnvironmentUtils.isBrowser();
const isMobile = Config.isMobile;
const gmaps = isBrowser ? window.google.maps : null;

function roundStartTime(datetime, timezone) {
    const dt = datetime ? moment(datetime) : moment().tz(timezone);

    return dt.minutes() < 30 ? dt.minute(0) : dt.minute(30);
}

const SearchUtils = {
    SEGMENT_SPOT_META: 'sh-segment-spot-meta',

    defaultSearchSettings: {
        searchType: 'address',
        showNoSpotsFound: false,
        currentIncrement: 0,
        zoom: 14,
        isDefaultTimes: true,
        overrideIdealZoom: false,
        destinationTitle: 'Destination',
        sortBy: 'distance',
        isInfoPanelVisible: isBrowser,
        breadcrumbs: null,
        searchMapInstance: null,
        geolocated: false,
        isMobileListViewActive: true,
        mobilePaginationLimit: 30,
    },

    prepareBreadcrumbs(initData, searchType) {
        const {
            city: {data: city},
            destination: {data: destination},
            searchRequest,
        } = initData;
        const {
            display_name: cityDisplayName,
            relative_url: relativeUrl,
            slug: citySlug,
            state,
        } = city;
        const cityUrl = `${Config.siteUrl}/city/${relativeUrl}`;
        const searchString = searchRequest ? searchRequest.search_string : null;
        const geolocate = searchRequest ? searchRequest.geolocate : false;

        return {
            extraBreadcrumb: {
                url:
                    searchType === 'event' || searchType === 'venue'
                        ? `${Config.siteUrl}/category/${citySlug}/theaters-and-venues-parking`
                        : null,
                label:
                    searchType === 'event' || searchType === 'venue'
                        ? 'Venues'
                        : null,
            },
            activeBreadcrumb: {
                url:
                    searchType === 'event'
                        ? `${Config.siteUrl}/destination/${citySlug}/${destination.relative_url}`
                        : null,
                label: geolocate
                    ? 'Current Location'
                    : searchType === 'city'
                    ? `${cityDisplayName}, ${state}`
                    : destination.title
                    ? `${destination.title}${
                          searchType === 'event' ? ' Events' : ''
                      }`
                    : searchString
                    ? searchString
                    : 'Downtown',
            },
            city: {
                url: geolocate ? null : cityUrl,
                displayName: geolocate ? null : cityDisplayName,
            },
        };
    },

    prepareStoreData({initData, pageType, currentSearchState = null}) {
        const {
            city: {data: city},
            destination: {data: destination},
            searchRequest,
        } = initData;
        const {
            display_name: cityDisplayName,
            zoom_level: cityZoom,
            default_map_zoom_level: cityDefaultMapZoom,
        } = city;
        const monthly = searchRequest ? searchRequest.monthly : false;
        const queryParams =
            !isBrowser && searchRequest.search_url
                ? UrlUtils.parseParams(searchRequest.search_url)
                : UrlUtils.parseParams(window.location.search);
        const searchString = searchRequest ? searchRequest.search_string : null;
        const overrideIdealZoom = destination.override_ideal_zoom || false;
        const hasDestination = !isEmpty(destination);

        const searchState = currentSearchState
            ? {
                  isPending: false,
                  data: {
                      ...SearchUtils.defaultSearchSettings,
                      searchMapInstance:
                          currentSearchState.data.searchMapInstance,
                      isMobileListViewActive:
                          currentSearchState.data.isMobileListViewActive,
                      isInfoPanelVisible: isBrowser,
                      sortBy: currentSearchState.data.sortBy,
                  },
                  error: null,
              }
            : {
                  isPending: false,
                  data: {
                      ...SearchUtils.defaultSearchSettings,
                      isMobileListViewActive: true,
                      isInfoPanelVisible: isBrowser,
                      sortBy: 'distance',
                  },
                  error: null,
              };
        let searchType = searchState.data.searchType;

        switch (pageType) {
            case 'city':
                searchType = 'city';
                break;

            case 'event':
                searchType = 'event';
                break;

            case 'destination':
                if (destination.has_upcoming_events) {
                    searchType = 'venue';
                } else {
                    searchType = 'destination';
                }

                break;

            default:
                searchType = 'address';
        }

        const breadcrumbs = this.prepareBreadcrumbs(initData, searchType);

        const initialState = {
            ...searchState,
            data: {
                ...searchState.data,
                searchType,
                breadcrumbs,
                overrideIdealZoom:
                    searchType === 'city' && cityZoom
                        ? true
                        : overrideIdealZoom,
                zoom:
                    searchType === 'city'
                        ? SearchUtils.getCityZoom({
                              city,
                              monthly,
                              mobile: isMobile,
                          })
                        : hasDestination
                        ? overrideIdealZoom &&
                          (destination.desktop_zoom_level ||
                              destination.mobile_zoom_level)
                            ? SearchUtils.getDestinationZoom({
                                  destination,
                                  monthly,
                                  mobile: isMobile,
                              })
                            : destination.default_map_zoom_level ||
                              cityDefaultMapZoom
                        : cityDefaultMapZoom,
                isDefaultTimes: isNil(queryParams.starts),
                referrer: queryParams.referrer,
                referrerAffiliate: queryParams.sha_affiliate,
                destinationTitle:
                    searchType === 'city'
                        ? cityDisplayName
                        : destination.title
                        ? destination.title
                        : !isEmpty(searchString)
                        ? searchString
                        : 'Destination',
                kmlMapLayerEnabled: hasDestination
                    ? destination.kml_map_layer_enabled
                    : false,
                kmlMapLayerURL: hasDestination
                    ? destination.kml_map_layer_url
                    : null,
                mobilePaginationLimit:
                    Config.mobilePaginationLimit ||
                    SearchUtils.defaultSearchSettings.mobilePaginationLimit,
            },
        };

        if (searchRequest && searchRequest.isSearchPage && isBrowser) {
            StorageUtils.set(
                'previous-search',
                {
                    url: `${window.location.pathname}${window.location.search}`,
                    title: initialState.data.destinationTitle,
                },
                'session'
            );
        }

        return initialState;
    },

    prepareRequest({requestType, bounds, searchState, searchRequestState}) {
        const newSearchState = {
            ...searchState,
            requestIncrements: 5,
            params: {
                ...searchRequestState,
                include: Config.facilityIncludes,
                sort: 'distance',
                source: isMobile ? 'web_mobile' : 'web',
                mobile_only: false, // eslint-disable-line camelcase
            },
        };

        if (requestType === 'tiled') {
            const {
                tiles,
                numRequests,
                innerCoords,
            } = SearchTileUtils.createTiles(bounds, newSearchState);

            newSearchState.tiles = tiles;
            newSearchState.requestIncrements = numRequests;

            if (innerCoords) {
                newSearchState.params.inner_coords = innerCoords; // eslint-disable-line camelcase
            }
        } else if (requestType === 'radial') {
            const center = bounds.getCenter();
            const maxDistance =
                gmaps.geometry.spherical.computeDistanceBetween(
                    center,
                    bounds.getNorthEast()
                ) * 0.9;
            const distanceDelta = (
                maxDistance / newSearchState.requestIncrements
            ).toFixed(2);
            const distances = [];
            let distanceIterator = 0;

            times(newSearchState.requestIncrements + 1, () => {
                distances.push(distanceDelta * distanceIterator);
                distanceIterator++;
            });

            newSearchState.params.latitude = center.lat();
            newSearchState.params.longitude = center.lng();
            newSearchState.radialDistances = distances;
        }

        return newSearchState;
    },

    updateRoute({currentUrl, searchParams, adParams}) {
        const {
            monthly,
            latitude,
            longitude,
            starts,
            ends,
            search_string: searchString,
            eid,
            operator_id: operatorId,
            rebook_reservation_id: rebookReservationId,
            airport,
            monthlyEnabled,
            page_info: pageInfo,
        } = searchParams;
        const hash = window.location.hash ? window.location.hash : '';
        const destination = pageInfo ? pageInfo.setup.destination : null;
        const urlParams = {...adParams};
        let cleanedUrl = currentUrl.split(/[?#]/)[0];

        if (!includes(cleanedUrl, 'parking-near-me')) {
            if (includes(cleanedUrl, '-parking')) {
                if (
                    (isNil(destination) && isNil(monthlyEnabled)) ||
                    monthlyEnabled ||
                    (destination && destination.monthly_enabled)
                ) {
                    if (monthly) {
                        if (!includes(cleanedUrl, '-monthly-parking')) {
                            cleanedUrl = cleanedUrl.replace(
                                '-parking',
                                '-monthly-parking'
                            );
                        }
                    } else {
                        cleanedUrl = cleanedUrl.replace(
                            '-monthly-parking',
                            '-parking'
                        );
                    }
                }
            } else {
                urlParams.latitude = latitude;
                urlParams.longitude = longitude;

                if (searchString) {
                    urlParams.search_string = searchString; // eslint-disable-line camelcase
                }

                if (monthly) {
                    urlParams.monthly = monthly;
                }
            }
        }

        urlParams.starts = starts;
        if (!monthly) {
            urlParams.ends = ends;
        }

        if (
            ((destination && destination.is_airport) || airport) &&
            starts &&
            ends &&
            !monthly
        ) {
            urlParams.airport = airport;
        }

        if (monthly) {
            delete urlParams.airport;
        }

        if (eid) {
            if (cleanedUrl.endsWith('-parking')) {
                urlParams.eid = eid;
            }
        }

        if (rebookReservationId) {
            urlParams.rebook_reservation_id = rebookReservationId; // eslint-disable-line camelcase
        }

        if (operatorId) {
            urlParams.operator_id = operatorId; // eslint-disable-line camelcase
        }

        return `${cleanedUrl}${
            size(urlParams)
                ? `?${UrlUtils.createQueryString(urlParams).replace(
                      /%3A%3A/g,
                      '::'
                  )}${hash}`
                : ''
        }`;
    },

    getCityZoom({city, monthly = false, mobile = false}) {
        if (mobile && monthly && city.monthly_mobile_zoom_level) {
            return city.monthly_mobile_zoom_level;
        } else if (mobile && city.mobile_zoom_level) {
            return city.mobile_zoom_level;
        } else if (monthly && city.monthly_zoom_level) {
            return city.monthly_zoom_level;
        } else {
            return city.zoom_level;
        }
    },

    getDestinationZoom({destination, monthly = false, mobile = false}) {
        if (mobile && monthly && destination.monthly_mobile_zoom_level) {
            return destination.monthly_mobile_zoom_level;
        } else if (mobile && destination.mobile_zoom_level) {
            return destination.mobile_zoom_level;
        } else if (monthly && destination.monthly_desktop_zoom_level) {
            return destination.monthly_desktop_zoom_level;
        } else {
            return destination.desktop_zoom_level;
        }
    },

    getReferrerDetails({referrer, previousRental = null}) {
        let fromScreen;

        switch (referrer) {
            case 'favorites':
                fromScreen = 'favorite';
                break;

            case 'list':
                fromScreen = 'list';
                break;

            case 'pin':
                fromScreen = 'map';
                break;

            case 'modal':
                fromScreen = 'product details';
                break;

            case 'widget':
                fromScreen = 'widget';
                break;

            default:
                fromScreen = 'checkout';
                break;
        }

        if (previousRental) {
            fromScreen = 'buy this spot again';
        }

        return fromScreen;
    },

    createTimeWindowParams(page, query, timezone) {
        const {starts: startsParam, ends: endsParam} = query;

        if (startsParam && endsParam) {
            return {starts: startsParam, ends: endsParam};
        }

        const starts = roundStartTime(startsParam, timezone);
        const ends = moment(starts).add(moment.duration(3, 'hours'));
        const airportEndDate = moment(starts).add(moment.duration(3, 'days'));

        if (page === Page.AIRPORT) {
            return {
                starts: DateUtils.combineDateAndTime(
                    starts,
                    TimeUtils.getAsMoment('07:00 AM')
                ).format(Config.apiDateTimeFormat),
                ends: DateUtils.combineDateAndTime(
                    airportEndDate,
                    TimeUtils.getAsMoment('11:00 PM')
                ).format(Config.apiDateTimeFormat),
            };
        } else {
            return {
                starts: starts.format(Config.apiDateTimeFormat),
                ends: ends.format(Config.apiDateTimeFormat),
            };
        }
    },
};

export default SearchUtils;
